/**
 * Self-registration client (marketplace-api `api/registration/*`).
 *
 * This is the native replacement for the ias-connect registration iframe, used on marketplaces whose
 * AuthMode is not `Current`. Every call is anonymous; progress between steps is carried by the
 * short-lived `registrationToken` the API returns from each step — there is no session, so the token
 * must be threaded from one call to the next.
 */

import { getMarketplaceApiBase } from './apiBase';

/** Account types, matching marketplace-api's RegistrationAccountType. */
export const ACCOUNT_TYPES = {
  AUCTION_ACCESS_DEALER: 'AuctionAccessDealer',
  DEALER: 'Dealer',
  PUBLIC: 'Public',
};

/**
 * Error codes marketplace-api returns for a step that cannot proceed. Mirrors
 * RegistrationErrorCodes server-side.
 */
export const REGISTRATION_ERRORS = {
  DISABLED: 'registration_disabled',
  INVALID_EMAIL: 'invalid_email',
  TERMS_REQUIRED: 'terms_required',
  ACCOUNT_EXISTS: 'account_exists',
  INVALID_PHONE_NUMBER: 'invalid_phone_number',
  PHONE_SEND_FAILED: 'phone_send_failed',
  PHONE_NOT_VERIFIED: 'phone_not_verified',
  INVALID_CODE: 'invalid_verification_code',
  AUCTION_ACCESS_INVALID: 'auction_access_invalid',
  AUCTION_ACCESS_UNAVAILABLE: 'auction_access_unavailable',
  ACCOUNT_TYPE_NOT_ALLOWED: 'account_type_not_allowed',
  MISSING_FIELDS: 'missing_required_fields',
  IDENTITY_SETUP_FAILED: 'identity_setup_failed',
  SESSION_EXPIRED: 'registration_session_expired',
  RATE_LIMITED: 'rate_limited',
};

/**
 * A registration step that failed for a reason the user can act on. `code` is one of
 * REGISTRATION_ERRORS; `message` is server-supplied and safe to display.
 */
export class RegistrationError extends Error {
  constructor(code, message) {
    super(message || 'Registration failed.');
    this.name = 'RegistrationError';
    this.code = code;
  }
}

/** URL for the AuctionACCESS enrollment page, offered when a rep can't be verified. */
export const AUCTION_ACCESS_ENROLL_URL = 'https://www.auctionaccess.com/registration';

async function request(path, { method = 'POST', body } = {}) {
  let response;
  try {
    response = await fetch(`${getMarketplaceApiBase()}/api/registration/${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    throw new RegistrationError('network_error', 'We couldn’t reach the server. Please try again.');
  }

  // 429 is produced by the rate limiter, which returns no body to parse.
  if (response.status === 429) {
    throw new RegistrationError(
      REGISTRATION_ERRORS.RATE_LIMITED,
      'Too many attempts. Please wait a few minutes and try again.'
    );
  }

  let data = null;
  try {
    data = await response.json();
  } catch (parseError) {
    data = null;
  }

  if (!response.ok) {
    throw new RegistrationError(
      (data && data.error) || `registration_failed_${response.status}`,
      data && data.error_description
    );
  }

  return data;
}

/**
 * Fetch the marketplace's registration configuration. Always resolves — an unavailable marketplace
 * comes back as `{ enabled: false }` so the caller can fall back to ias-connect rather than error.
 * @returns {Promise<object>}
 */
export async function fetchRegistrationConfig() {
  try {
    return await request('config', { method: 'GET' });
  } catch (error) {
    // Treat any failure as "not available here"; the caller falls back to the ias-connect iframe.
    return { enabled: false };
  }
}

/**
 * Open a registration session for an email address.
 * @param {string} email
 * @param {boolean} acceptedTerms
 * @returns {Promise<{registrationToken: string, phoneVerificationRequired: boolean}>}
 */
export function startRegistration(email, acceptedTerms) {
  return request('start', { body: { email, acceptedTerms } });
}

/**
 * Text a confirmation code to a mobile number.
 * @param {string} registrationToken
 * @param {string} phoneNumber E.164, e.g. +14155550123
 * @returns {Promise<{registrationToken: string}>}
 */
export function sendPhoneCode(registrationToken, phoneNumber) {
  return request('phone/send', { body: { registrationToken, phoneNumber } });
}

/**
 * Confirm the code the registrant received.
 * @param {string} registrationToken
 * @param {string} code
 * @returns {Promise<{registrationToken: string}>}
 */
export function verifyPhoneCode(registrationToken, code) {
  return request('phone/verify', { body: { registrationToken, code } });
}

/**
 * Validate AuctionACCESS credentials, returning the rep's name and their dealerships.
 * @param {string} registrationToken
 * @param {string} auctionAccessRepId 9-digit rep number
 * @param {string} governmentId last 4 of passport / driver's licence
 * @returns {Promise<{registrationToken: string, firstName: string, lastName: string, dealers: Array}>}
 */
export function verifyAuctionAccess(registrationToken, auctionAccessRepId, governmentId) {
  return request('auction-access/verify', {
    body: { registrationToken, auctionAccessRepId, governmentId },
  });
}

/**
 * Create the account. On success the registrant is emailed a link to set their password, after which
 * they sign in through the normal SSO flow.
 * @param {string} registrationToken
 * @param {string} accountType one of ACCOUNT_TYPES
 * @param {object} details the collected field values
 * @returns {Promise<{status: string, email: string}>}
 */
export function completeRegistration(registrationToken, accountType, details) {
  return request('complete', { body: { registrationToken, accountType, details } });
}

/**
 * Compose an E.164 number from a dial code and the digits the user typed.
 * @param {string} dialCode e.g. "1"
 * @param {string} localNumber whatever the user typed, punctuation included
 * @returns {string} e.g. "+14155550123"
 */
export function toE164(dialCode, localNumber) {
  const digits = String(localNumber || '').replace(/\D/g, '');
  return digits ? `+${dialCode}${digits}` : '';
}

/**
 * Loose E.164 check, matching the server's guard. Real validation is the SMS itself — a number that
 * passes this and isn't reachable simply fails at send time.
 * @param {string} value
 * @returns {boolean}
 */
export function isPlausibleE164(value) {
  return /^\+[1-9]\d{6,14}$/.test(String(value || ''));
}
